import { type Context, useCallback, useContext, useEffect, useState } from 'react'
import type { VBarStackContext } from './VBarStackContext'
import type { UseVBarStackItemParameters, VBarStackItem } from '../types'

/**
 * This hook can be used to register/update/remove an item from the parent VBarStack.
 * Communication with the parent stack is done through the `VBarStackContext`.
 * If the parent stack is not present, nothing will will happen, and the returned state
 * will be initialized to visible with the provided parameters.
 * If the parent stack is present, the context will be `connected`, and the item will be registered
 * with the stack, and the state will be updated when hook parameters change.
 * The initial item state will be `hidden` and the stack will be providing state changes to update the item state.
 */
export function useVBarStackItem<ClusterItemMeta>(
  VBarStackContext: Context<VBarStackContext<ClusterItemMeta>>,
  params: UseVBarStackItemParameters<ClusterItemMeta>,
): VBarStackItem {
  const context = useContext(VBarStackContext)
  if (!context) {
    throw new Error('useVBarStackItem must be used in the subtree of a VBarStack component')
  }

  // Item state, if not connected, we initialize it to visible with the provided parameters.
  // If connected, we initialize it to hidden, and the parent VBarStack will provide state changes.
  const [state, setState] = useState<VBarStackItem>({
    kind: context.isConnected ? 'collapsed' : 'expanded',
    top: params.top,
    anchorHeight: params.anchorHeight,
    maxHeight: params.minContentHeight,
    clusterId: null,
    clusterOffset: 0,
    isConnected: context.isConnected,
  })

  // Callback invoked by the parent VBarStack when the item state changes.
  const onStateChanged = useCallback((state: VBarStackItem) => {
    setState(previous => {
      if (
        previous.kind === state.kind &&
        previous.top === state.top &&
        previous.anchorHeight === state.anchorHeight &&
        previous.maxHeight === state.maxHeight &&
        previous.clusterOffset === state.clusterOffset &&
        previous.clusterId === state.clusterId &&
        previous.isConnected === state.isConnected
      ) {
        return previous
      }

      return state
    })
  }, [])

  // Upsert item when the hook parameters change.
  useEffect(() => {
    const id = params.id
    if (!context.isConnected) {
      return
    }
    context.upsert({
      id,
      active: params.active,
      top: params.top,
      anchorHeight: params.anchorHeight,
      minContentHeight: params.minContentHeight,
      clusterItemData: params.clusterItemData,
      onStateChanged,
    })
  }, [
    context,
    params.id,
    params.active,
    params.top,
    params.anchorHeight,
    params.minContentHeight,
    params.clusterItemData,
    onStateChanged,
  ])

  // Remove item when the component is unmounted or the id changes.
  useEffect(() => {
    const id = params.id
    return () => {
      if (context.isConnected) {
        context.remove(id)
      }
    }
  }, [context, params.id])

  return state
}
